import { db } from '../../firebase/firebase';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { encryptEmail, verifyEmailAsync } from '../../utilities/emailEncryption';
import { getOrgIdsWithRole } from './userOrganisations';

/**
 * User/project-admin email mutations and checks, extracted from UserManager.
 * All functions talk to Firestore directly and mirror the original
 * UserManager method bodies exactly.
 */

/**
 * Updates a user's email address with consent tracking
 * For facilitators, stores the email encrypted in Organisations[orgId].email field
 * @param userId The user ID to update
 * @param email The new email address
 * @param consentVersion Optional consent version string (defaults to '1.0')
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateUserEmail = async (userId: string, email: string, consentVersion: string = '1.0'): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    const organisations = userData.Organisations || {};

    // Check if user is a facilitator in any organisation
    const facilitatorOrgIds = getOrgIdsWithRole(organisations, 'facilitator');

    if (facilitatorOrgIds.length > 0) {
      // For facilitators, update email in Organisations[orgId].email (encrypted)
      const encryptedEmail = await encryptEmail(email.trim().toLowerCase());
      const updatedOrgs = { ...organisations };

      facilitatorOrgIds.forEach(orgId => {
        updatedOrgs[orgId] = {
          ...updatedOrgs[orgId],
          email: encryptedEmail,
          emailConsentGiven: new Date().toISOString(),
          emailConsentVersion: consentVersion
        };
      });

      await updateDoc(userDocRef, {
        Organisations: updatedOrgs,
        emailConsentGiven: new Date().toISOString(),
        emailConsentVersion: consentVersion,
        updatedAt: Timestamp.now()
      });
    } else {
      // For non-facilitators, update the top-level email field
      await updateDoc(userDocRef, {
        email: email.trim(),
        emailConsentGiven: new Date().toISOString(),
        emailConsentVersion: consentVersion,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error("Error updating user email:", error);
    throw error;
  }
};

/**
 * Updates the project admin email address (encrypted) in the user document
 * @param userId The user ID to update
 * @param email The email address to encrypt and store
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateProjectAdminEmail = async (userId: string, email: string): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);

    // Encrypt the email before storing
    const encryptedEmail = await encryptEmail(email.trim());

    await updateDoc(userDocRef, {
      projectAdminEmail: encryptedEmail,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error updating project admin email:", error);
    throw error;
  }
};

/**
 * Checks if a project admin has an email address set
 * @param projectId The project ID to check
 * @param userEmail The email to match against stored project admin emails
 * @returns A Promise that resolves to an object with hasEmail flag and optionally the decrypted email
 */
export const checkProjectAdminEmail = async (projectId: string, userEmail: string): Promise<{ hasEmail: boolean; email?: string }> => {
  try {
    // Get the project
    const projectDocRef = doc(db, 'Projects', projectId);
    const projectDoc = await getDoc(projectDocRef);

    if (!projectDoc.exists()) {
      return {
        hasEmail: false
      };
    }

    const projectData = projectDoc.data();
    const projectAdminEmails = projectData.projectAdminEmail || [];

    if (!Array.isArray(projectAdminEmails) || projectAdminEmails.length === 0) {
      return {
        hasEmail: false
      };
    }

    const normalizedUserEmail = userEmail.toLowerCase();

    // Try decrypting all emails and comparing with user's email
    for (const encryptedEmail of projectAdminEmails) {
      try {
        const decryptedEmail = await verifyEmailAsync(encryptedEmail);
        if (decryptedEmail.toLowerCase() === normalizedUserEmail) {
          return {
            hasEmail: true,
            email: decryptedEmail
          };
        }
      } catch (decryptError) {
        // If decryption fails, try direct comparison (in case it's already plain text)
        // or continue to next email
        if (encryptedEmail.toLowerCase() === normalizedUserEmail) {
          return {
            hasEmail: true,
            email: encryptedEmail
          };
        }
        continue;
      }
    }

    // Also try encrypting user email and comparing (for cases where encryption is consistent)
    try {
      const encryptedUserEmail = await encryptEmail(normalizedUserEmail);
      if (projectAdminEmails.includes(encryptedUserEmail)) {
        return {
          hasEmail: true,
          email: userEmail
        };
      }
    } catch (encryptError) {
      // If encryption fails, we've already checked decryption above
      console.warn("Could not encrypt user email for comparison:", encryptError);
    }

    return {
      hasEmail: false
    };
  } catch (error) {
    console.error("Error checking project admin email:", error);
    return {
      hasEmail: false
    };
  }
};

/**
 * Updates the user's email reminder frequency preference
 * @param userId The UID of the user
 * @param frequency The reminder frequency ('none' | 'weekly' | 'fortnightly' | 'monthly')
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateEmailReminderFrequency = async (userId: string, frequency: 'none' | 'weekly' | 'fortnightly' | 'monthly'): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    await updateDoc(userDocRef, {
      emailReminderFrequency: frequency,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error updating email reminder frequency:", error);
    throw error;
  }
};
